/**
 * Plain geometry helpers, ported from the prototype (index.html) with the same meaning.
 * Points are [x, y] in metres. Nothing here knows about the plan document.
 */
import type { Pt } from '../../shared/types';

/** A point that is only read. Immer drafts and frozen docs both fit. */
export type ReadPt = readonly [number, number];

/** Samples per span of a Catmull-Rom road or river curve. Node j of a road sits at sample j*SEG. */
export const SEG = 10;

/** Clamps v to [lo, hi]. */
export function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

/** Distance between two points. */
export function dist(a: ReadPt, b: ReadPt): number {
  return Math.hypot(b[0] - a[0], b[1] - a[1]);
}

const at = <T>(arr: readonly T[], i: number): T => arr[i] as T;

/**
 * Catmull-Rom curve through `pts` with `seg` samples per span. An open curve ends exactly on the last point;
 * a closed one has n*seg samples and does not repeat the first point.
 */
export function spline(pts: readonly ReadPt[], closed: boolean, seg = SEG): Pt[] {
  const n = pts.length;
  if (n < 2) return pts.map(p => [p[0], p[1]]);
  const get = (i: number) => at(pts, closed ? ((i % n) + n) % n : clamp(i, 0, n - 1));
  const last = closed ? n : n - 1, out: Pt[] = [];
  for (let i = 0; i < last; i++) {
    const p0 = get(i - 1), p1 = get(i), p2 = get(i + 1), p3 = get(i + 2);
    for (let s = 0; s < seg; s++) {
      const t = s / seg, t2 = t * t, t3 = t2 * t;
      const c = (d: 0 | 1) => 0.5 * ((2 * p1[d]) + (-p0[d] + p2[d]) * t + (2 * p0[d] - 5 * p1[d] + 4 * p2[d] - p3[d]) * t2 + (-p0[d] + 3 * p1[d] - 3 * p2[d] + p3[d]) * t3);
      out.push([c(0), c(1)]);
    }
  }
  if (!closed) { const e = at(pts, n - 1); out.push([e[0], e[1]]); }
  return out;
}

/**
 * Corner-cutting smoothing of a closed ring. Points flagged in `fix` stay put (and stay in the result),
 * so a corner where three areas meet stays shared, and a wall shared by two areas smooths the same on both sides.
 */
export function chaikin(pts: readonly ReadPt[], fix: readonly boolean[] | null, iters: number): Pt[] {
  let P: Pt[] = pts.map(p => [p[0], p[1]]);
  let F: boolean[] = fix && fix.length === pts.length ? fix.slice() : pts.map(() => false);
  for (let it = 0; it < iters; it++) {
    const Q: Pt[] = [], G: boolean[] = [], n = P.length;
    for (let i = 0; i < n; i++) {
      const a = at(P, i), b = at(P, (i + 1) % n);
      if (F[i]) { Q.push(a); G.push(true); }
      Q.push([a[0] * 0.75 + b[0] * 0.25, a[1] * 0.75 + b[1] * 0.25]); G.push(false);
      Q.push([a[0] * 0.25 + b[0] * 0.75, a[1] * 0.25 + b[1] * 0.75]); G.push(false);
    }
    P = Q; F = G;
  }
  return P;
}

/** Point in polygon (even-odd rule). */
export function pip(poly: readonly ReadPt[], x: number, y: number): boolean {
  let c = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const [xi, yi] = at(poly, i), [xj, yj] = at(poly, j);
    if ((yi > y) !== (yj > y) && x < (xj - xi) * (y - yi) / (yj - yi) + xi) c = !c;
  }
  return c;
}

/** Area of a closed polygon in m² (always positive). */
export function polyArea(poly: readonly ReadPt[]): number {
  let a = 0;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const pi = at(poly, i), pj = at(poly, j);
    a += (pj[0] + pi[0]) * (pj[1] - pi[1]);
  }
  return Math.abs(a / 2);
}

/** Average of the points (not the area centroid, the same as the prototype). */
export function centroid(pts: readonly ReadPt[]): Pt {
  let x = 0, y = 0;
  for (const p of pts) { x += p[0]; y += p[1]; }
  return [x / pts.length, y / pts.length];
}

/** Distance from (px, py) to segment ab. */
export function distSeg(px: number, py: number, a: ReadPt, b: ReadPt): number {
  const dx = b[0] - a[0], dy = b[1] - a[1], l = dx * dx + dy * dy;
  const t = clamp(l ? ((px - a[0]) * dx + (py - a[1]) * dy) / l : 0, 0, 1);
  return Math.hypot(px - a[0] - t * dx, py - a[1] - t * dy);
}

/** Distance from (x, y) to a polyline, or to a ring when `closed`. 1e9 for fewer than 2 points. */
export function distPoly(poly: readonly ReadPt[], x: number, y: number, closed: boolean): number {
  let d = 1e9;
  const n = poly.length;
  for (let i = 0; i < (closed ? n : n - 1); i++) d = Math.min(d, distSeg(x, y, at(poly, i), at(poly, (i + 1) % n)));
  return d;
}

/** Index of the point nearest to (x, y) and its distance. */
export function nearestIdx(pts: readonly ReadPt[], x: number, y: number): [number, number] {
  let b = 0, bd = 1e9;
  pts.forEach((p, i) => { const d = Math.hypot(p[0] - x, p[1] - y); if (d < bd) { bd = d; b = i; } });
  return [b, bd];
}

/** Length of a polyline. */
export function polyLen(poly: readonly ReadPt[]): number {
  let l = 0;
  for (let i = 1; i < poly.length; i++) l += dist(at(poly, i - 1), at(poly, i));
  return l;
}

/** Bounding box [minX, minY, maxX, maxY]. */
export function bbox(poly: readonly ReadPt[]): [number, number, number, number] {
  let a = 1e9, b = 1e9, c = -1e9, d = -1e9;
  for (const p of poly) { a = Math.min(a, p[0]); b = Math.min(b, p[1]); c = Math.max(c, p[0]); d = Math.max(d, p[1]); }
  return [a, b, c, d];
}

/** Intersection point of segments ab and cd, or null. */
export function segX(a: ReadPt, b: ReadPt, c: ReadPt, d: ReadPt): Pt | null {
  const r1 = b[0] - a[0], r2 = b[1] - a[1], s1 = d[0] - c[0], s2 = d[1] - c[1], den = r1 * s2 - r2 * s1;
  if (!den) return null;
  const t = ((c[0] - a[0]) * s2 - (c[1] - a[1]) * s1) / den, u = ((c[0] - a[0]) * r2 - (c[1] - a[1]) * r1) / den;
  return t >= 0 && t <= 1 && u >= 0 && u <= 1 ? [a[0] + t * r1, a[1] + t * r2] : null;
}

/** The nearest spot on a polyline: segment index i, fraction t along it, the spot, its distance and the segment's unit normal. */
export interface PolyHit { d: number; i: number; t: number; px: number; py: number; nx: number; ny: number }

/** Nearest spot on polyline P to (x, y). Zero-length segments are skipped. */
export function nearestOnPoly(P: readonly ReadPt[], x: number, y: number): PolyHit {
  let best: PolyHit = { d: 1e9, i: 0, t: 0, px: x, py: y, nx: 0, ny: 1 };
  for (let i = 0; i < P.length - 1; i++) {
    const a = at(P, i), b = at(P, i + 1), dx = b[0] - a[0], dy = b[1] - a[1], l2 = dx * dx + dy * dy;
    if (!l2) continue;
    const t = clamp(((x - a[0]) * dx + (y - a[1]) * dy) / l2, 0, 1), px = a[0] + t * dx, py = a[1] + t * dy;
    const d = Math.hypot(x - px, y - py), l = Math.sqrt(l2);
    if (d < best.d) best = { d, i, t, px, py, nx: -dy / l, ny: dx / l };
  }
  return best;
}

/**
 * Spreads one value per control point over a sampled curve (SEG samples per span), linearly between points,
 * the same way `spline` samples it. `samples` is the sample count of the curve.
 */
export function spreadAlong(vals: readonly number[], closed: boolean, samples: number, seg = SEG): number[] {
  const n = vals.length, span = closed ? n : n - 1, out: number[] = [];
  for (let k = 0; k < samples; k++) {
    if (k >= span * seg) { out.push(at(vals, closed ? 0 : n - 1)); continue; }
    const j = Math.floor(k / seg), t = (k % seg) / seg;
    out.push(at(vals, j) * (1 - t) + at(vals, (j + 1) % n) * t);
  }
  return out;
}

/** Value of a per-sample array at segment i, fraction t (as returned by nearestOnPoly). */
export function lerpAt(vals: readonly number[], i: number, t: number): number {
  const a = vals[i] ?? 0, b = vals[Math.min(i + 1, vals.length - 1)] ?? a;
  return a * (1 - t) + b * t;
}

/** Cumulative length at every point of a polyline (first is 0). */
export function cumLengths(P: readonly ReadPt[]): number[] {
  const cum = [0];
  for (let i = 1; i < P.length; i++) cum.push(at(cum, i - 1) + dist(at(P, i - 1), at(P, i)));
  return cum;
}

/** The point at distance s along polyline P with cumulative lengths `cum` (clamped to the ends). */
export function pointAlong(P: readonly ReadPt[], cum: readonly number[], s: number): Pt {
  for (let i = 0; i < P.length - 1; i++) {
    const c0 = at(cum, i), c1 = at(cum, i + 1);
    if (c1 >= s) {
      const t = clamp((s - c0) / ((c1 - c0) || 1), 0, 1), a = at(P, i), b = at(P, i + 1);
      return [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t];
    }
  }
  const e = at(P, P.length - 1);
  return [e[0], e[1]];
}
